/**
 * FrontEndTab · v2.6.9 batch BQ
 * ==============================
 * Admin Console → Administration → Front End. Edits everything on the
 * public marketing site (Home / About / Services / Vehicles / Contact /
 * Global Settings). Saves to /admin/site/config.
 *
 * Layout: nested sub-tabs across the top, large editable form per section.
 * Save / Reset / Sync Prices controls in the action bar.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, RotateCcw, ExternalLink, Plus, Trash2, RefreshCw, AlertTriangle, ArrowUp, ArrowDown, Globe, Home, Info, Briefcase, Car, MessageSquare, Image as ImageIcon, Palette, MapPin, Users, UserCog, Sparkles, Megaphone } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";
import RichTextEditor from "@/components/admin/RichTextEditor";
import OurTeamEditor from "@/components/admin/OurTeamEditor";
import PremiumServicesEditor from "@/components/admin/PremiumServicesEditor";
import GroupRidesEditor from "@/components/admin/GroupRidesEditor";
import HomeBannerEditor from "@/components/admin/HomeBannerEditor";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { invalidateSiteConfig } from "@/hooks/useSiteConfig";
import { clearPageVisibilityCache } from "@/components/PageGate";

const ICON_CHOICES = ["DollarSign", "ShieldCheck", "Car", "Clock", "MapPin", "Plane"];

// v2.6.9 batch BQ-3 · Curated 1600 px Unsplash hero presets — one-click set
// for the home page background. URLs include a w=1600 query so they load fast
// even with the dark overlay applied on the live site.
const HERO_PRESETS = [
  { label: "Night City",   url: "https://images.unsplash.com/photo-1559762717-99c81ac85459?w=1600&q=80" },
  { label: "Yellow Cab",   url: "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1600&q=80" },
  { label: "Open Road",    url: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=80" },
  { label: "Rainy Street", url: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1600&q=80" },
];

export default function FrontEndTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sub, setSub] = useState("global");

  const load = () => {
    setLoading(true);
    api.get("/site/config")
      .then((r) => { setCfg(r.data); setDirty(false); })
      .catch(() => toast.error("Could not load site config"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const mutate = (fn) => {
    setCfg((c) => {
      const next = JSON.parse(JSON.stringify(c));
      fn(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/site/config", cfg);
      invalidateSiteConfig();
      toast.success("Site saved — public pages will refresh on next load");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the entire site to factory defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/site/reset");
      setCfg(data.config); invalidateSiteConfig(); setDirty(false);
      toast.success("Site reset to defaults");
    } catch { toast.error("Reset failed"); } finally { setSaving(false); }
  };

  if (loading || !cfg) return <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="fe-loading"><Loader2 className="h-7 w-7 animate-spin mx-auto"/></Card>;

  return (
    <div className="space-y-4" data-testid="frontend-tab">
      {/* Header / actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold flex items-center gap-2"><Globe className="h-5 w-5 text-amber-400"/>Front End</h2>
          <p className="text-xs text-zinc-400">Manage every word + image on the public marketing site. Changes go live on next visitor load.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1" data-testid="fe-preview-link"><ExternalLink className="h-3 w-3"/>Open site</a>
          {dirty && <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold" data-testid="fe-dirty-flag">● Unsaved</span>}
          <Button data-testid="fe-reset" onClick={reset} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-300"><RotateCcw className="h-4 w-4 mr-1.5"/>Reset</Button>
          <Button data-testid="fe-save" onClick={save} disabled={!dirty || saving} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Save className="h-4 w-4 mr-1.5"/>Save All</>}
          </Button>
        </div>
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-zinc-950 border border-zinc-800 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 gap-1 h-auto p-1">
          <TabsTrigger value="global"     data-testid="fe-tab-global"     className="text-xs gap-1"><Globe className="h-3.5 w-3.5"/>Global</TabsTrigger>
          <TabsTrigger value="home"       data-testid="fe-tab-home"       className="text-xs gap-1"><Home className="h-3.5 w-3.5"/>Home</TabsTrigger>
          <TabsTrigger value="banner"     data-testid="fe-tab-banner"     className="text-xs gap-1"><Megaphone className="h-3.5 w-3.5"/>CTA Banner</TabsTrigger>
          <TabsTrigger value="about"      data-testid="fe-tab-about"      className="text-xs gap-1"><Info className="h-3.5 w-3.5"/>About</TabsTrigger>
          <TabsTrigger value="our-team"   data-testid="fe-tab-our-team"   className="text-xs gap-1"><UserCog className="h-3.5 w-3.5"/>Our Team</TabsTrigger>
          <TabsTrigger value="services"   data-testid="fe-tab-services"   className="text-xs gap-1"><Briefcase className="h-3.5 w-3.5"/>Services</TabsTrigger>
          <TabsTrigger value="vehicles"   data-testid="fe-tab-vehicles"   className="text-xs gap-1"><Car className="h-3.5 w-3.5"/>Vehicles</TabsTrigger>
          <TabsTrigger value="tourism"    data-testid="fe-tab-tourism"    className="text-xs gap-1"><MapPin className="h-3.5 w-3.5"/>Tourism</TabsTrigger>
          <TabsTrigger value="contact"    data-testid="fe-tab-contact"    className="text-xs gap-1"><MessageSquare className="h-3.5 w-3.5"/>Contact</TabsTrigger>
          <TabsTrigger value="premium"    data-testid="fe-tab-premium"    className="text-xs gap-1"><Sparkles className="h-3.5 w-3.5"/>Premium</TabsTrigger>
          <TabsTrigger value="group"      data-testid="fe-tab-group"      className="text-xs gap-1"><Users className="h-3.5 w-3.5"/>Group Rides</TabsTrigger>
        </TabsList>

        {/* ── GLOBAL ────────────────────────────────────────────────── */}
        <TabsContent value="global">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-global-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Global Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Site name"><Input data-testid="fe-site-name" value={cfg.global.site_name} onChange={(e) => mutate((c) => { c.global.site_name = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Header logo URL"><Input data-testid="fe-logo-url" value={cfg.global.logo_url} onChange={(e) => mutate((c) => { c.global.logo_url = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Phone (display)"><Input data-testid="fe-phone" value={cfg.global.phone} onChange={(e) => mutate((c) => { c.global.phone = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Email"><Input data-testid="fe-email" value={cfg.global.email} onChange={(e) => mutate((c) => { c.global.email = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Address / Service area" className="md:col-span-2"><Input data-testid="fe-address" value={cfg.global.address} onChange={(e) => mutate((c) => { c.global.address = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["facebook", "instagram", "twitter", "tiktok", "youtube"].map((s) => (
                <Field key={s} label={`${s.charAt(0).toUpperCase()}${s.slice(1)} URL`}>
                  <Input data-testid={`fe-social-${s}`} value={cfg.global.social_links?.[s] || ""} onChange={(e) => mutate((c) => { c.global.social_links = { ...c.global.social_links, [s]: e.target.value }; })} placeholder="https://..." className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                </Field>
              ))}
            </div>
            <Field label="Footer copyright"><Input data-testid="fe-footer-copy" value={cfg.global.footer_copyright} onChange={(e) => mutate((c) => { c.global.footer_copyright = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>

            {/* v2.7.65+E192 · Public Website Logo — customer-facing
                logo shown on the PUBLIC marketing site navigation menu.
                Placed ABOVE the Admin Console Logo so the more customer-
                visible asset is edited first. Never affects the admin
                console sidebar, login pages, or any other admin-only
                imagery. */}
            <PublicWebsiteLogoCard />

            {/* v2.7.65+E193 · Login Page Logo — hero mark shown on the
                rider, driver, dispatch/admin, and customer sign-in
                screens. Uploads swap ONLY that field; no other login-
                page copy, colors, or backgrounds change. */}
            <LoginPageLogoCard />

            {/* v2.7.65+E188 · Admin Console Logo — INTERNAL branding for
                the left-side admin sidebar. Never touches the public
                marketing logo, customer booking pages, login pages, or
                any customer-facing branding. */}
            <AdminConsoleLogoCard />

            {/* v2.6.9 batch BS · Footer background — image OR color */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-footer-bg">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Footer Background</div>
                  <div className="text-[11px] text-zinc-500">Choose a solid color OR an uploaded image for the global footer.</div>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900 rounded-md p-1 border border-zinc-800" data-testid="fe-footer-bg-mode">
                  <button
                    type="button"
                    data-testid="fe-footer-mode-color"
                    onClick={() => mutate((c) => { c.global.footer_use_color = true; })}
                    className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 transition-colors ${cfg.global.footer_use_color ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
                  >
                    <Palette className="h-3 w-3"/>Color
                  </button>
                  <button
                    type="button"
                    data-testid="fe-footer-mode-image"
                    onClick={() => mutate((c) => { c.global.footer_use_color = false; })}
                    className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 transition-colors ${!cfg.global.footer_use_color ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
                  >
                    <ImageIcon className="h-3 w-3"/>Image
                  </button>
                </div>
              </div>
              {cfg.global.footer_use_color ? (
                <div className="flex items-center gap-3" data-testid="fe-footer-color">
                  <input
                    type="color"
                    value={cfg.global.footer_bg_color || "#0a0a0a"}
                    onChange={(e) => mutate((c) => { c.global.footer_bg_color = e.target.value; })}
                    className="h-12 w-20 rounded border border-zinc-700 bg-zinc-900 cursor-pointer"
                    data-testid="fe-footer-color-picker"
                  />
                  <Input
                    value={cfg.global.footer_bg_color || "#0a0a0a"}
                    onChange={(e) => mutate((c) => { c.global.footer_bg_color = e.target.value; })}
                    placeholder="#0a0a0a"
                    className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-[180px] meter-display"
                  />
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Solid Footer Color</span>
                </div>
              ) : (
                <ImageUploader
                  value={cfg.global.footer_bg_image_url}
                  onChange={(url) => mutate((c) => { c.global.footer_bg_image_url = url; })}
                  label="Footer Background Image"
                  testid="fe-footer-bg-img"
                  purpose="footer_bg"
                  height={120}
                />
              )}
            </Card>

            {/* v2.6.9 batch CA · Per-portal login backdrops (Rider /
                Driver / Dispatch). Each maps to its own /portal/login route
                and falls back to /login-bg.jpg if blank. */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-login-bgs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Portal Login Backdrops</div>
                <div className="text-[11px] text-zinc-500">Independent background image for each login page. Up to 100 MB per upload.</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ImageUploader
                  value={cfg.global.login_rider_bg_url || ""}
                  onChange={(url) => mutate((c) => { c.global.login_rider_bg_url = url; })}
                  label="Rider Login (/ride/login)"
                  testid="fe-login-rider-bg"
                  purpose="login_rider"
                  height={130}
                />
                <ImageUploader
                  value={cfg.global.login_driver_bg_url || ""}
                  onChange={(url) => mutate((c) => { c.global.login_driver_bg_url = url; })}
                  label="Driver Login (/login)"
                  testid="fe-login-driver-bg"
                  purpose="login_driver"
                  height={130}
                />
                <ImageUploader
                  value={cfg.global.login_dispatch_bg_url || ""}
                  onChange={(url) => mutate((c) => { c.global.login_dispatch_bg_url = url; })}
                  label="Dispatch Login (/admin/login)"
                  testid="fe-login-dispatch-bg"
                  purpose="login_dispatch"
                  height={130}
                />
              </div>
            </Card>

            {/* v2.6.9 batch BU · NAV background image (asset key "NAV") */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-nav-bg">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Header Nav Background (NAV)</div>
                <div className="text-[11px] text-zinc-500">Full-width image rendered behind the main top navigation bar. Recommended ~1920×120px.</div>
              </div>
              <ImageUploader
                value={cfg.global.nav_background_url}
                onChange={(url) => mutate((c) => { c.global.nav_background_url = url; })}
                label="NAV Background Image"
                testid="fe-nav-bg-img"
                purpose="nav_bg"
                height={120}
              />
            </Card>

            {/* v2.6.9 batch BU · Bottom-of-site Bison co-branding strip */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-bison-strip">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Footer Co-Branding Strip</div>
                <div className="text-[11px] text-zinc-500">Three brand marks displayed at the bottom of the global footer below the copyright line. Clear any URL to hide that individual mark.</div>
              </div>
              <Field label="Powered-by Headline Text">
                <Input
                  data-testid="fe-bison-powered-by-text"
                  value={cfg.global.bison_powered_by_text || ""}
                  onChange={(e) => mutate((c) => { c.global.bison_powered_by_text = e.target.value; })}
                  placeholder="TAXI DISPATCH POWERED BY"
                  className="bg-zinc-900 border-zinc-700 text-zinc-100"
                />
              </Field>
              {/* v2.6.9 batch CF · 3-column grid — TAXICOMMANDER · BISON AI ·
                  TAXICOMMANDER GPS. Same uploader UI for each. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ImageUploader
                  value={cfg.global.taxi_commander_logo_url || ""}
                  onChange={(url) => mutate((c) => { c.global.taxi_commander_logo_url = url; })}
                  label="TAXICOMMANDER Logo"
                  testid="fe-taxi-commander"
                  purpose="taxi_commander"
                  height={120}
                />
                <ImageUploader
                  value={cfg.global.bison_ai_logo_url}
                  onChange={(url) => mutate((c) => { c.global.bison_ai_logo_url = url; })}
                  label="Bison AI Logo"
                  testid="fe-bison-ai-logo"
                  purpose="bison_ai"
                  height={120}
                />
                <ImageUploader
                  value={cfg.global.taxi_commander_gps_logo_url || ""}
                  onChange={(url) => mutate((c) => { c.global.taxi_commander_gps_logo_url = url; })}
                  label="TAXICOMMANDER GPS Logo"
                  testid="fe-taxi-commander-gps"
                  purpose="taxi_commander_gps"
                  height={120}
                />
              </div>
            </Card>

            <div className="flex items-center gap-3 pt-2 border-t border-zinc-800" data-testid="fe-mobile-redirect">
              <Switch checked={cfg.global.mobile_redirect_enabled !== false} onCheckedChange={(v) => mutate((c) => { c.global.mobile_redirect_enabled = v; })} id="fe-mobile-toggle"/>
              <label htmlFor="fe-mobile-toggle" className="text-sm text-zinc-200 font-semibold">Auto-redirect smartphones to /ride/login</label>
            </div>
            {/* v2.6.9 batch CH · "Ready to ride?" CTA strip toggle. Defaults
                OFF — strip hides on every marketing page until re-enabled. */}
            <div className="flex items-center gap-3" data-testid="fe-ready-cta-toggle">
              <Switch
                checked={cfg.global.show_ready_to_ride_cta === true}
                onCheckedChange={(v) => mutate((c) => { c.global.show_ready_to_ride_cta = v; })}
                id="fe-ready-cta"
              />
              <label htmlFor="fe-ready-cta" className="text-sm text-zinc-200 font-semibold">Show "Ready for your next ride?" yellow CTA strip on marketing pages</label>
            </div>
          </Card>

          {/* v2.7.0 batch DI · Per-Page Visibility Toggles. Independent of the
              CMS content fields; controls whether a top-level marketing route
              even renders for visitors. Saves to its own admin endpoint so
              you can hide e.g. "Tourism" overnight without touching content. */}
          <PageVisibilityCard/>
        </TabsContent>

        {/* ── HOME ──────────────────────────────────────────────────── */}
        <TabsContent value="home">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-home-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Home Page</h3>

            {/* v2.6.9 batch BS · Hero background — upload OR URL paste */}
            <Card className="bg-zinc-950 border-2 border-amber-400/40 p-4" data-testid="fe-hero-bg-editor">
              <ImageUploader
                value={cfg.home.hero_image_url}
                onChange={(url) => mutate((c) => { c.home.hero_image_url = url; })}
                label="Hero Background Photo (full image behind the booking form)"
                testid="fe-hero-img"
                purpose="hero"
                height={192}
              />
              {/* One-click presets */}
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">Quick Presets</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="fe-hero-presets">
                  {HERO_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`fe-hero-preset-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => mutate((c) => { c.home.hero_image_url = p.url; })}
                      className={`relative rounded-md overflow-hidden border-2 transition-all aspect-video group ${
                        cfg.home.hero_image_url === p.url ? "border-amber-400 ring-2 ring-amber-400/40" : "border-zinc-700 hover:border-amber-400/60"
                      }`}
                    >
                      <img src={p.url} alt={p.label} className="w-full h-full object-cover"/>
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors"/>
                      <div className="absolute bottom-1 left-1.5 right-1.5 text-[10px] uppercase tracking-wider font-bold text-white drop-shadow">{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Hero eyebrow"><Input value={cfg.home.hero_eyebrow} onChange={(e) => mutate((c) => { c.home.hero_eyebrow = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Hero title"><Input value={cfg.home.hero_title} onChange={(e) => mutate((c) => { c.home.hero_title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Hero subtitle" className="md:col-span-2"><Textarea rows={2} value={cfg.home.hero_subtitle} onChange={(e) => mutate((c) => { c.home.hero_subtitle = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="CTA heading"><Input data-testid="fe-cta-head" value={cfg.home.cta_heading} onChange={(e) => mutate((c) => { c.home.cta_heading = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="CTA phone (display)"><Input data-testid="fe-cta-phone" value={cfg.home.cta_phone} onChange={(e) => mutate((c) => { c.home.cta_phone = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="CTA sub-text" className="md:col-span-2"><Input value={cfg.home.cta_subtext} onChange={(e) => mutate((c) => { c.home.cta_subtext = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
              <Switch checked={cfg.home.show_pricing !== false} onCheckedChange={(v) => mutate((c) => { c.home.show_pricing = v; })} id="fe-show-pricing"/>
              <label htmlFor="fe-show-pricing" className="text-sm text-zinc-200 font-semibold">Show pricing section on Home</label>
            </div>

            <div className="pt-3 mt-3 border-t border-zinc-800">
              <Field label="Why Choose Buller — section heading"><Input value={cfg.home.why_choose_heading} onChange={(e) => mutate((c) => { c.home.why_choose_heading = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <div className="space-y-3 mt-3" data-testid="fe-why-items">
                {cfg.home.why_choose_items.map((it, i) => (
                  <Card key={`why__${it.title || "untitled"}__${i}`} className="bg-zinc-950 border-zinc-800 p-3" data-testid={`fe-why-item-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <select value={it.icon} onChange={(e) => mutate((c) => { c.home.why_choose_items[i].icon = e.target.value; })} className="col-span-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-2 text-xs text-zinc-100" title="Fallback icon (used only when no badge image is uploaded)">
                        {ICON_CHOICES.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                      <Input className="col-span-3 bg-zinc-900 border-zinc-700 text-zinc-100" value={it.title} placeholder="Title" onChange={(e) => mutate((c) => { c.home.why_choose_items[i].title = e.target.value; })}/>
                      <Input className="col-span-6 bg-zinc-900 border-zinc-700 text-zinc-100" value={it.desc || it.description || ""} placeholder="Description" onChange={(e) => mutate((c) => { c.home.why_choose_items[i].desc = e.target.value; })}/>
                      <Button variant="ghost" size="icon" className="col-span-1 text-red-400" disabled={cfg.home.why_choose_items.length <= 1} onClick={() => mutate((c) => { c.home.why_choose_items.splice(i, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
                    </div>
                    {/* v2.6.9 batch CF · Optional badge image upload. When set
                        it OVERRIDES the lucide icon on the public site. */}
                    <div className="mt-3">
                      <ImageUploader
                        label="Badge Image (overrides icon when set)"
                        value={it.image_url || ""}
                        onChange={(url) => mutate((c) => { c.home.why_choose_items[i].image_url = url; })}
                        testid={`fe-why-item-${i}-badge`}
                        purpose={`why_choose_${i}`}
                        height={110}
                      />
                    </div>
                  </Card>
                ))}
                <Button data-testid="fe-why-add" variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { c.home.why_choose_items.push({ icon: "ShieldCheck", title: "New feature", desc: "", image_url: "" }); })}><Plus className="h-3 w-3 mr-1"/>Add Feature</Button>
              </div>
            </div>

            <div className="pt-3 mt-3 border-t border-zinc-800 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Service Area heading"><Input value={cfg.home.service_area_heading} onChange={(e) => mutate((c) => { c.home.service_area_heading = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
                <Field label="Service Area subtitle"><Input value={cfg.home.service_area_subtitle} onChange={(e) => mutate((c) => { c.home.service_area_subtitle = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              </div>

              {/* v2.6.9 batch BS · City Logo (rendered above the home map) */}
              <Card className="bg-zinc-950 border border-zinc-800 p-4" data-testid="fe-city-logo-block">
                <ImageUploader
                  value={cfg.home.service_area_logo_url}
                  onChange={(url) => mutate((c) => { c.home.service_area_logo_url = url; })}
                  label="City Logo (renders above the Service Area map)"
                  testid="fe-city-logo"
                  purpose="city_logo"
                  height={120}
                />
              </Card>

              {/* v2.6.9 batch BS · Service Area background — image OR color */}
              <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-service-area-bg">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Service Area Background</div>
                    <div className="text-[11px] text-zinc-500">Choose either a solid color OR an uploaded image.</div>
                  </div>
                  <div className="flex items-center gap-2 bg-zinc-900 rounded-md p-1 border border-zinc-800" data-testid="fe-service-area-bg-mode">
                    <button
                      type="button"
                      data-testid="fe-service-area-mode-color"
                      onClick={() => mutate((c) => { c.home.service_area_use_color = true; })}
                      className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 transition-colors ${cfg.home.service_area_use_color ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
                    >
                      <Palette className="h-3 w-3"/>Color
                    </button>
                    <button
                      type="button"
                      data-testid="fe-service-area-mode-image"
                      onClick={() => mutate((c) => { c.home.service_area_use_color = false; })}
                      className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 transition-colors ${!cfg.home.service_area_use_color ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
                    >
                      <ImageIcon className="h-3 w-3"/>Image
                    </button>
                  </div>
                </div>
                {cfg.home.service_area_use_color ? (
                  <div className="flex items-center gap-3" data-testid="fe-service-area-color">
                    <input
                      type="color"
                      value={cfg.home.service_area_bg_color || "#000000"}
                      onChange={(e) => mutate((c) => { c.home.service_area_bg_color = e.target.value; })}
                      className="h-12 w-20 rounded border border-zinc-700 bg-zinc-900 cursor-pointer"
                      data-testid="fe-service-area-color-picker"
                    />
                    <Input
                      value={cfg.home.service_area_bg_color || "#000000"}
                      onChange={(e) => mutate((c) => { c.home.service_area_bg_color = e.target.value; })}
                      placeholder="#000000"
                      className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-[180px] meter-display"
                    />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Solid Background Color</span>
                  </div>
                ) : (
                  <ImageUploader
                    value={cfg.home.service_area_bg_image_url}
                    onChange={(url) => mutate((c) => { c.home.service_area_bg_image_url = url; })}
                    label="Service Area Background Image"
                    testid="fe-service-area-bg-img"
                    purpose="service_area_bg"
                    height={160}
                  />
                )}
              </Card>

              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Destinations we cover</Label>
              <div className="space-y-1.5" data-testid="fe-destinations">
                {cfg.home.service_area_destinations.map((d, i) => (
                  <div key={`dest__${d}__${i}`} className="flex items-center gap-2">
                    <Input value={d} onChange={(e) => mutate((c) => { c.home.service_area_destinations[i] = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                    <Button variant="ghost" size="icon" className="text-red-400" disabled={cfg.home.service_area_destinations.length <= 1} onClick={() => mutate((c) => { c.home.service_area_destinations.splice(i, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { c.home.service_area_destinations.push("New destination"); })}><Plus className="h-3 w-3 mr-1"/>Add Destination</Button>
              </div>
            </div>

            {/* v2.8 · PARTNER AD BANNER — "Catch our ad on the following" shown
                under the home Pick-Your-Portal buttons + clickable station links. */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3 mt-3" data-testid="fe-portal-ad-banner">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Partner Ad Banner</div>
                  <div className="text-[11px] text-zinc-500">Shown under the "Pick Your Portal" login buttons on the home page.</div>
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-200 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={cfg.home.portal_ad_banner_enabled !== false}
                    onChange={(e) => mutate((c) => { c.home.portal_ad_banner_enabled = e.target.checked; })}
                    data-testid="fe-portal-ad-enabled"
                  />
                  Show banner
                </label>
              </div>
              <ImageUploader
                value={cfg.home.portal_ad_banner_url || ""}
                onChange={(url) => mutate((c) => { c.home.portal_ad_banner_url = url; })}
                label="Banner image"
                testid="fe-portal-ad-image"
                purpose="portal_ad_banner"
                height={140}
              />
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Whole-banner link (optional)</Label>
                <Input
                  value={cfg.home.portal_ad_banner_link || ""}
                  placeholder="https://wright.media"
                  onChange={(e) => mutate((c) => { c.home.portal_ad_banner_link = e.target.value; })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100 mt-1.5"
                  data-testid="fe-portal-ad-link"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Clickable station links</Label>
                <div className="space-y-1.5 mt-1.5" data-testid="fe-portal-ad-links">
                  {(cfg.home.portal_ad_banner_links || []).map((s, i) => (
                    <div key={`station__${i}`} className="flex items-center gap-2">
                      <Input value={s.label || ""} placeholder="Station name" onChange={(e) => mutate((c) => { c.home.portal_ad_banner_links[i].label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                      <Input value={s.url || ""} placeholder="https://station.com" onChange={(e) => mutate((c) => { c.home.portal_ad_banner_links[i].url = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                      <Button variant="ghost" size="icon" className="text-red-400" onClick={() => mutate((c) => { c.home.portal_ad_banner_links.splice(i, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { if (!Array.isArray(c.home.portal_ad_banner_links)) c.home.portal_ad_banner_links = []; c.home.portal_ad_banner_links.push({ label: "New station", url: "" }); })}><Plus className="h-3 w-3 mr-1"/>Add Station</Button>
                </div>
              </div>
            </Card>

            {/* v2.8 · BISON TAXI COMMANDER — "Our Fleet Technology Partners"
                banner (About menu page). CMS-managed image + optional link. */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3 mt-3" data-testid="fe-btc-partners-banner">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Fleet Technology Partners Banner</div>
                  <div className="text-[11px] text-zinc-500">Bottom of the Bison Taxi Commander page (About menu).</div>
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-200 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={(cfg.bison_taxi_commander || {}).partners_banner_enabled !== false}
                    onChange={(e) => mutate((c) => { if (!c.bison_taxi_commander) c.bison_taxi_commander = {}; c.bison_taxi_commander.partners_banner_enabled = e.target.checked; })}
                    data-testid="fe-btc-partners-enabled"
                  />
                  Show banner
                </label>
              </div>
              <ImageUploader
                value={(cfg.bison_taxi_commander || {}).partners_banner_url || ""}
                onChange={(url) => mutate((c) => { if (!c.bison_taxi_commander) c.bison_taxi_commander = {}; c.bison_taxi_commander.partners_banner_url = url; })}
                label="Banner image"
                testid="fe-btc-partners-image"
                purpose="btc_partners_banner"
                height={140}
              />
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Banner link (optional)</Label>
                <Input
                  value={(cfg.bison_taxi_commander || {}).partners_banner_link || ""}
                  placeholder="https://partner-site.com"
                  onChange={(e) => mutate((c) => { if (!c.bison_taxi_commander) c.bison_taxi_commander = {}; c.bison_taxi_commander.partners_banner_link = e.target.value; })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100 mt-1.5"
                  data-testid="fe-btc-partners-link"
                />
              </div>
            </Card>


            {/* v2.6.9 batch BU · QUICK FARE BACKGROUND */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3 mt-3" data-testid="fe-quick-fare-bg">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Quick Fare Background (FARE BACKGROUND)</div>
                <div className="text-[11px] text-zinc-500">Image rendered behind the homepage "Get a Fare In Seconds" booking card.</div>
              </div>
              <ImageUploader
                value={cfg.home.quick_fare_background_url}
                onChange={(url) => mutate((c) => { c.home.quick_fare_background_url = url; })}
                label="Quick Fare Background"
                testid="fe-quick-fare-bg-img"
                purpose="quick_fare_bg"
                height={140}
              />
            </Card>

            {/* v2.6.9 batch BU · Portal Quick-Links strip (Rider / Driver / Dispatch) */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-portal-quicklinks">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Portal Quick-Links (Above Why-Choose)</div>
                  <div className="text-[11px] text-zinc-500">Three large image-buttons linking to the rider, driver, and dispatch portals.</div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={cfg.home.portal_quicklinks_enabled !== false}
                    onCheckedChange={(v) => mutate((c) => { c.home.portal_quicklinks_enabled = v; })}
                    id="fe-portal-toggle"
                    data-testid="fe-portal-quicklinks-toggle"
                  />
                  <label htmlFor="fe-portal-toggle" className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Enabled</label>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ImageUploader
                  value={cfg.home.portal_quicklink_rider_url}
                  onChange={(url) => mutate((c) => { c.home.portal_quicklink_rider_url = url; })}
                  label="RIDER Login Button"
                  testid="fe-quicklink-rider"
                  purpose="quicklink_rider"
                  height={120}
                />
                <ImageUploader
                  value={cfg.home.portal_quicklink_driver_url}
                  onChange={(url) => mutate((c) => { c.home.portal_quicklink_driver_url = url; })}
                  label="DRIVER Login Button"
                  testid="fe-quicklink-driver"
                  purpose="quicklink_driver"
                  height={120}
                />
                <ImageUploader
                  value={cfg.home.portal_quicklink_dispatch_url}
                  onChange={(url) => mutate((c) => { c.home.portal_quicklink_dispatch_url = url; })}
                  label="DISPATCH Login Button"
                  testid="fe-quicklink-dispatch"
                  purpose="quicklink_dispatch"
                  height={120}
                />
              </div>
            </Card>

            {/* v2.6.9 batch BU · "Where We Go" 3-column city grid */}
            <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-city-grid">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">"Where We Go" City Grid (Under Why-Choose)</div>
                  <div className="text-[11px] text-zinc-500">3-column image gallery with BOOK NOW overlay buttons.</div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={cfg.home.city_grid_enabled !== false}
                    onCheckedChange={(v) => mutate((c) => { c.home.city_grid_enabled = v; })}
                    id="fe-city-grid-toggle"
                    data-testid="fe-city-grid-toggle"
                  />
                  <label htmlFor="fe-city-grid-toggle" className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Enabled</label>
                </div>
              </div>
              <Field label="Section Heading">
                <Input
                  data-testid="fe-city-grid-heading"
                  value={cfg.home.city_grid_heading || ""}
                  onChange={(e) => mutate((c) => { c.home.city_grid_heading = e.target.value; })}
                  placeholder="Where We Go"
                  className="bg-zinc-900 border-zinc-700 text-zinc-100"
                />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(cfg.home.city_grid || []).map((c2, idx) => (
                  <Card key={c2.slug || `city-${idx}`} className="bg-zinc-900 border-zinc-800 p-3 space-y-2" data-testid={`fe-city-grid-${idx}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">City #{idx + 1} · slug: {c2.slug || "—"}</div>
                    <Field label="Display Name">
                      <Input
                        data-testid={`fe-city-grid-${idx}-name`}
                        value={c2.name || ""}
                        onChange={(e) => mutate((c) => { c.home.city_grid[idx].name = e.target.value; })}
                        className="bg-zinc-950 border-zinc-700 text-zinc-100"
                      />
                    </Field>
                    <Field label="Tagline">
                      <Input
                        data-testid={`fe-city-grid-${idx}-tagline`}
                        value={c2.tagline || ""}
                        onChange={(e) => mutate((c) => { c.home.city_grid[idx].tagline = e.target.value; })}
                        className="bg-zinc-950 border-zinc-700 text-zinc-100"
                      />
                    </Field>
                    <Field label="Meter Variant">
                      <select
                        data-testid={`fe-city-grid-${idx}-meter`}
                        value={c2.meter || "statewide"}
                        onChange={(e) => mutate((c) => { c.home.city_grid[idx].meter = e.target.value; })}
                        className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
                      >
                        <option value="city">City (Weatherford)</option>
                        <option value="statewide">Statewide</option>
                      </select>
                    </Field>
                    <Field label="Destination Query (sent to /ride/login)">
                      <Input
                        data-testid={`fe-city-grid-${idx}-dest`}
                        value={c2.destination_query || ""}
                        onChange={(e) => mutate((c) => { c.home.city_grid[idx].destination_query = e.target.value; })}
                        placeholder="Oklahoma City, OK"
                        className="bg-zinc-950 border-zinc-700 text-zinc-100"
                      />
                    </Field>
                    <ImageUploader
                      value={c2.image_url}
                      onChange={(url) => mutate((c) => { c.home.city_grid[idx].image_url = url; })}
                      label="City Photo"
                      testid={`fe-city-grid-${idx}-image`}
                      purpose={`city_${c2.slug || idx}`}
                      height={120}
                    />
                  </Card>
                ))}
              </div>
            </Card>
          </Card>
        </TabsContent>

        {/* ── ABOUT ────────────────────────────────────────────────── */}
        <TabsContent value="about">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-about-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">About Page</h3>
            <Field label="Title"><Input data-testid="fe-about-title" value={cfg.about.title} onChange={(e) => mutate((c) => { c.about.title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            <Field label="Subtitle"><Input value={cfg.about.subtitle} onChange={(e) => mutate((c) => { c.about.subtitle = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            <Field label="Body (supports line breaks)"><Textarea data-testid="fe-about-body" rows={10} value={cfg.about.body} onChange={(e) => mutate((c) => { c.about.body = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            {/* v2.6.9 batch CG · Full-width header image for /about. */}
            <ImageUploader
              label="About Page Header Image (full-width)"
              value={cfg.about.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.about.header_image_url = url; })}
              testid="fe-about-header"
              purpose="about_header"
              height={140}
            />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Feature images (uploaded)</Label>
            <div className="space-y-2">
              {(cfg.about.feature_images || []).map((url, i) => (
                <div key={`feat__${url || "empty"}__${i}`} className="flex items-start gap-2">
                  <div className="flex-1">
                    {/* v2.6.9 batch CG · URL Input REPLACED with direct file
                        upload — image-link inputs are fully retired from the
                        platform per spec. */}
                    <ImageUploader
                      label={`Feature Image ${i + 1}`}
                      value={url}
                      onChange={(newUrl) => mutate((c) => { c.about.feature_images[i] = newUrl; })}
                      testid={`fe-about-feature-${i}`}
                      purpose={`about_feature_${i}`}
                      height={110}
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-400 mt-7" onClick={() => mutate((c) => { c.about.feature_images.splice(i, 1); })} data-testid={`fe-about-feature-${i}-delete`}><Trash2 className="h-3.5 w-3.5"/></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { c.about.feature_images = [...(c.about.feature_images || []), ""]; })} data-testid="fe-about-feature-add"><Plus className="h-3 w-3 mr-1"/>Add Image</Button>
            </div>
          </Card>
        </TabsContent>

        {/* ── LEADERSHIP ──────────────────────────────────────────── */}
        <TabsContent value="our-team">
          <OurTeamEditor/>
        </TabsContent>

        {/* ── SERVICES ────────────────────────────────────────────── */}
        <TabsContent value="services">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-services-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Services Page</h3>
            <Field label="Title"><Input value={cfg.services.title} onChange={(e) => mutate((c) => { c.services.title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            <Field label="Intro"><Textarea rows={2} value={cfg.services.intro} onChange={(e) => mutate((c) => { c.services.intro = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            {/* v2.6.9 batch CG · Page-level header + "What We Offer" cards
                background image — both direct uploads (no URL inputs). */}
            <ImageUploader
              label="Services Page Header Image (full-width)"
              value={cfg.services.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.services.header_image_url = url; })}
              testid="fe-services-header"
              purpose="services_header"
              height={140}
            />
            <ImageUploader
              label='"What We Offer" Cards Background Image'
              value={cfg.services.cards_bg_image_url || ""}
              onChange={(url) => mutate((c) => { c.services.cards_bg_image_url = url; })}
              testid="fe-services-cards-bg"
              purpose="services_cards_bg"
              height={140}
            />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Services</Label>
            <div className="space-y-3">
              {cfg.services.services.map((svc, i) => (
                <Card key={`svc-${svc.name || i}-${i}`} className="bg-zinc-950 border-zinc-800 p-3" data-testid={`fe-service-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input value={svc.name} placeholder="Name" onChange={(e) => mutate((c) => { c.services.services[i].name = e.target.value; })} className="bg-black border-zinc-700 text-zinc-100"/>
                    <Input value={svc.price} placeholder="Price (display)" onChange={(e) => mutate((c) => { c.services.services[i].price = e.target.value; })} className="bg-black border-zinc-700 text-zinc-100 md:col-span-2"/>
                  </div>
                  <Textarea rows={2} className="mt-2 bg-black border-zinc-700 text-zinc-100" value={svc.description} placeholder="Description" onChange={(e) => mutate((c) => { c.services.services[i].description = e.target.value; })}/>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => mutate((c) => { [c.services.services[i - 1], c.services.services[i]] = [c.services.services[i], c.services.services[i - 1]]; })}><ArrowUp className="h-3.5 w-3.5"/></Button>
                    <Button variant="ghost" size="icon" disabled={i === cfg.services.services.length - 1} onClick={() => mutate((c) => { [c.services.services[i + 1], c.services.services[i]] = [c.services.services[i], c.services.services[i + 1]]; })}><ArrowDown className="h-3.5 w-3.5"/></Button>
                    <Button variant="ghost" size="icon" className="text-red-400" disabled={cfg.services.services.length <= 1} onClick={() => mutate((c) => { c.services.services.splice(i, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
                  </div>
                </Card>
              ))}
              <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { c.services.services.push({ name: "New service", description: "", price: "$0.00" }); })}><Plus className="h-3 w-3 mr-1"/>Add Service</Button>
            </div>
          </Card>
        </TabsContent>

        {/* ── VEHICLES ──────────────────────────────────────────── */}
        <TabsContent value="vehicles">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-vehicles-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Vehicles Page</h3>
            <Field label="Title"><Input value={cfg.vehicles.title} onChange={(e) => mutate((c) => { c.vehicles.title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            <Field label="Intro"><Textarea rows={2} value={cfg.vehicles.intro} onChange={(e) => mutate((c) => { c.vehicles.intro = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            {/* v2.6.9 batch CG · Full-width header image for /vehicles. */}
            <ImageUploader
              label="Vehicles Page Header Image (full-width)"
              value={cfg.vehicles.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.vehicles.header_image_url = url; })}
              testid="fe-vehicles-header"
              purpose="vehicles_header"
              height={140}
            />
            <div className="space-y-3">
              {cfg.vehicles.vehicles.map((v, i) => (
                <Card key={`veh-${v.name || i}-${i}`} className="bg-zinc-950 border-zinc-800 p-3" data-testid={`fe-vehicle-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input value={v.name} placeholder="Name" onChange={(e) => mutate((c) => { c.vehicles.vehicles[i].name = e.target.value; })} className="bg-black border-zinc-700 text-zinc-100"/>
                    <Input value={v.capacity} placeholder="Capacity" onChange={(e) => mutate((c) => { c.vehicles.vehicles[i].capacity = e.target.value; })} className="bg-black border-zinc-700 text-zinc-100"/>
                  </div>
                  <Textarea rows={2} className="mt-2 bg-black border-zinc-700 text-zinc-100" value={v.description} placeholder="Description" onChange={(e) => mutate((c) => { c.vehicles.vehicles[i].description = e.target.value; })}/>
                  {/* v2.6.9 batch CF · Replaced the image-URL <Input> with the
                      ImageUploader so admins upload an actual image instead of
                      pasting a hosted URL. URL input is still available inside
                      the uploader for advanced cases. */}
                  <div className="mt-3">
                    <ImageUploader
                      label="Vehicle Photo"
                      value={v.image_url || ""}
                      onChange={(url) => mutate((c) => { c.vehicles.vehicles[i].image_url = url; })}
                      testid={`fe-vehicle-${i}-image`}
                      purpose={`vehicle_${i}`}
                      height={140}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <Button variant="ghost" size="icon" className="text-red-400" disabled={cfg.vehicles.vehicles.length <= 1} onClick={() => mutate((c) => { c.vehicles.vehicles.splice(i, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
                  </div>
                </Card>
              ))}
              <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => mutate((c) => { c.vehicles.vehicles.push({ name: "New vehicle", capacity: "Up to 5", description: "", image_url: "" }); })}><Plus className="h-3 w-3 mr-1"/>Add Vehicle</Button>
            </div>
          </Card>
        </TabsContent>

        {/* ── TOURISM ─────────────────────────────────────────────── */}
        <TabsContent value="tourism">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4 mt-3" data-testid="fe-tourism-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Tourism Page · /tourism + auto-generated /tourism/&lt;slug&gt; per city</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Page Title">
                <Input
                  data-testid="fe-tourism-title"
                  value={(cfg.tourism?.title) || ""}
                  onChange={(e) => mutate((c) => { c.tourism = c.tourism || { title: "", intro: "", cities: [] }; c.tourism.title = e.target.value; })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100"
                />
              </Field>
              <Field label="Intro / Page Subtitle">
                <Textarea
                  data-testid="fe-tourism-intro"
                  rows={2}
                  value={(cfg.tourism?.intro) || ""}
                  onChange={(e) => mutate((c) => { c.tourism = c.tourism || { title: "", intro: "", cities: [] }; c.tourism.intro = e.target.value; })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100"
                />
              </Field>
            </div>
            {/* v2.6.9 batch CF · Full-width header image for the Tourism INDEX
                page (separate from each city's individual hero). */}
            <ImageUploader
              label="Tourism Page Header Image (full-width)"
              value={cfg.tourism?.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.tourism = c.tourism || { title: "Local Tourism", intro: "", cities: [] }; c.tourism.header_image_url = url; })}
              testid="fe-tourism-header-image"
              purpose="tourism_header"
              height={140}
            />

            <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Partner Cities ({(cfg.tourism?.cities || []).length}) — each city auto-generates its own /tourism/&lt;slug&gt; page</Label>
              <Button
                size="sm"
                data-testid="fe-tourism-add-city"
                onClick={() => mutate((c) => {
                  c.tourism = c.tourism || { title: "Local Tourism", intro: "", cities: [] };
                  c.tourism.cities = [...(c.tourism.cities || []), {
                    slug: `partner-${(c.tourism.cities || []).length + 1}`,
                    name: "New Partner City",
                    tagline: "",
                    hero_image_url: "",
                    intro_short: "",
                    body: "",
                    intro: "",
                    attractions: [],
                    enabled: true,
                  }];
                })}
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
              >
                <Plus className="h-3.5 w-3.5 mr-1"/>Add City
              </Button>
            </div>

            {(cfg.tourism?.cities || []).map((city, idx) => (
              <Card key={city.slug || `tour-city-${idx}`} className="bg-zinc-950 border-zinc-800 p-4 space-y-3" data-testid={`fe-tourism-city-${idx}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">City #{idx + 1} · /tourism/{city.slug || "(set-slug)"}</div>
                  <div className="flex items-center gap-1">
                    <div className="flex items-center gap-1.5 mr-2">
                      <Switch
                        data-testid={`fe-tourism-city-${idx}-enabled`}
                        checked={city.enabled !== false}
                        onCheckedChange={(v) => mutate((c) => { c.tourism.cities[idx].enabled = v; })}
                      />
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{city.enabled !== false ? "ON" : "OFF"}</span>
                    </div>
                    {idx > 0 && (
                      <Button size="icon" variant="ghost" data-testid={`fe-tourism-city-${idx}-up`} onClick={() => mutate((c) => { const arr = c.tourism.cities; [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; })}>
                        <ArrowUp className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {idx < (cfg.tourism?.cities || []).length - 1 && (
                      <Button size="icon" variant="ghost" data-testid={`fe-tourism-city-${idx}-down`} onClick={() => mutate((c) => { const arr = c.tourism.cities; [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]]; })}>
                        <ArrowDown className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`fe-tourism-city-${idx}-delete`}
                      onClick={() => { if (window.confirm(`Delete ${city.name || "this city"}?`)) mutate((c) => { c.tourism.cities = c.tourism.cities.filter((_, i) => i !== idx); }); }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-3.5 w-3.5"/>
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Display Name">
                    <Input
                      data-testid={`fe-tourism-city-${idx}-name`}
                      value={city.name || ""}
                      onChange={(e) => mutate((c) => { c.tourism.cities[idx].name = e.target.value; })}
                      className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    />
                  </Field>
                  <Field label="URL Slug">
                    <Input
                      data-testid={`fe-tourism-city-${idx}-slug`}
                      value={city.slug || ""}
                      onChange={(e) => mutate((c) => { c.tourism.cities[idx].slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-"); })}
                      className="bg-zinc-900 border-zinc-700 text-zinc-100"
                      placeholder="weatherford-ok"
                    />
                  </Field>
                  <Field label="Tagline" className="md:col-span-2">
                    <Input
                      data-testid={`fe-tourism-city-${idx}-tagline`}
                      value={city.tagline || ""}
                      onChange={(e) => mutate((c) => { c.tourism.cities[idx].tagline = e.target.value; })}
                      className="bg-zinc-900 border-zinc-700 text-zinc-100"
                      placeholder="Home of Route 66"
                    />
                  </Field>
                </div>
                <ImageUploader
                  label="Hero Image (shown on city card + city page)"
                  value={city.hero_image_url || ""}
                  onChange={(url) => mutate((c) => { c.tourism.cities[idx].hero_image_url = url; })}
                  testid={`fe-tourism-city-${idx}-hero`}
                />
                <Field label="Short Intro (1 sentence — shown on the /tourism index card)">
                  <Textarea
                    data-testid={`fe-tourism-city-${idx}-intro-short`}
                    rows={2}
                    value={city.intro_short || city.intro || ""}
                    onChange={(e) => mutate((c) => { c.tourism.cities[idx].intro_short = e.target.value; })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    placeholder="A quick teaser — 1-2 sentences shown on the city card."
                  />
                </Field>
                {/* v2.6.9 batch CF · Full rich-text body for the per-city
                    detail page at /tourism/<slug>. Replaces the plain intro
                    textarea. */}
                <RichTextEditor
                  label="Full Page Body (renders on /tourism/<slug>)"
                  value={city.body || ""}
                  onChange={(html) => mutate((c) => { c.tourism.cities[idx].body = html; })}
                  testid={`fe-tourism-city-${idx}-body`}
                  minHeight={240}
                />
                <Field label="Attractions (one per line)">
                  <Textarea
                    data-testid={`fe-tourism-city-${idx}-attractions`}
                    rows={4}
                    value={(city.attractions || []).join("\n")}
                    onChange={(e) => mutate((c) => { c.tourism.cities[idx].attractions = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean); })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono text-xs"
                    placeholder={"Stafford Air & Space Museum\nSWOSU Campus\nRoute 66 Heritage Walk"}
                  />
                </Field>
              </Card>
            ))}
            {(!cfg.tourism?.cities || cfg.tourism.cities.length === 0) && (
              <div className="text-center py-8 text-zinc-500 text-sm" data-testid="fe-tourism-empty">
                No partner cities yet. Click "Add City" to create one.
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ── CONTACT ─────────────────────────────────────────────── */}
        <TabsContent value="contact">
          <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3 mt-3" data-testid="fe-contact-card">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Contact Page</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Title"><Input value={cfg.contact.title} onChange={(e) => mutate((c) => { c.contact.title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Intro"><Input value={cfg.contact.intro} onChange={(e) => mutate((c) => { c.contact.intro = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Phone label"><Input value={cfg.contact.phone_label} onChange={(e) => mutate((c) => { c.contact.phone_label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Email label"><Input value={cfg.contact.email_label} onChange={(e) => mutate((c) => { c.contact.email_label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Submit button label"><Input value={cfg.contact.submit_button_label} onChange={(e) => mutate((c) => { c.contact.submit_button_label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
              <Field label="Success message"><Input value={cfg.contact.success_message} onChange={(e) => mutate((c) => { c.contact.success_message = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/></Field>
            </div>
            {/* v2.6.9 batch CG · Full-width header image for /contact. */}
            <ImageUploader
              label="Contact Page Header Image (full-width)"
              value={cfg.contact.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.contact.header_image_url = url; })}
              testid="fe-contact-header"
              purpose="contact_header"
              height={140}
            />
            {/* v2.6.9 batch CG · Taxi Rates page header image lives in
                cfg.rates.header_image_url (separate from this Contact tab,
                but co-located here because rates has no other CMS fields). */}
            <ImageUploader
              label="Taxi Rates Page Header Image (full-width) — applies to /rates"
              value={cfg.rates?.header_image_url || ""}
              onChange={(url) => mutate((c) => { c.rates = c.rates || { header_image_url: "" }; c.rates.header_image_url = url; })}
              testid="fe-rates-header"
              purpose="rates_header"
              height={140}
            />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-2">Form field labels</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(cfg.contact.form_labels || {}).map(([k, v]) => (
                <Field key={k} label={k}>
                  <Input value={v} onChange={(e) => mutate((c) => { c.contact.form_labels[k] = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                </Field>
              ))}
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="premium">
          <PremiumServicesEditor/>
        </TabsContent>
        <TabsContent value="group">
          <GroupRidesEditor/>
        </TabsContent>
        <TabsContent value="banner">
          <HomeBannerEditor/>
        </TabsContent>
      </Tabs>

      <Card className="bg-zinc-950 border-zinc-800 p-4 flex items-center justify-between gap-2 flex-wrap" data-testid="fe-utilities">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <AlertTriangle className="h-4 w-4 text-amber-400"/>
          Taxi rates live on a separate CMS at <code className="bg-zinc-900 px-1 rounded text-amber-300">Administration → Rider Price Page</code>.
        </div>
        <Button
          data-testid="fe-sync-prices"
          variant="outline"
          className="bg-zinc-950 border-amber-400/40 text-amber-300"
          onClick={() => { invalidateSiteConfig(); toast.success("Prices synced — rider Book-a-Ride form will reload rates on next quote"); }}
        >
          <RefreshCw className="h-4 w-4 mr-1.5"/>Sync Prices Now
        </Button>
      </Card>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}


// ============================================================================
// v2.7.0 batch DI · Per-Page Visibility Toggles
// ----------------------------------------------------------------------------
// Independent of /admin/site/config — this card writes to /admin/page-visibility
// so an admin can hide e.g. Tourism without affecting any other content. Reads
// from /site/page-visibility on mount and on every save. Unknown keys are
// silently dropped by the backend per the fallback rule in the spec.
// ============================================================================
const PAGE_TOGGLES = [
  { key: "home",          label: "Home / Landing Page",  hint: "Includes the QUICK BOOKING form",                  path: "/" },
  { key: "leadership",    label: "Leadership",           hint: "/leadership profile grid + detail pages",          path: "/leadership" },
  { key: "drivers",       label: "Drivers",              hint: "/drivers profile grid + detail pages",             path: "/drivers" },
  { key: "services",      label: "Services",             hint: "/services overview",                                path: "/services" },
  { key: "vehicles",      label: "Vehicles",             hint: "/vehicles fleet showcase",                          path: "/vehicles" },
  { key: "tourism",       label: "Tourism",              hint: "/tourism city grid + per-city pages",               path: "/tourism" },
  { key: "about",         label: "About Us",             hint: "/about company info",                               path: "/about" },
  { key: "contact",       label: "Contact Us",           hint: "/contact form + map",                               path: "/contact" },
  { key: "legal_privacy", label: "Privacy Policy",       hint: "/legal/privacy",                                    path: "/legal/privacy" },
  { key: "legal_terms",   label: "Terms of Service",     hint: "/legal/terms",                                      path: "/legal/terms" },
];

function PageVisibilityCard() {
  const [vis, setVis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/site/page-visibility")
      .then((r) => setVis(r.data || {}))
      .catch(() => toast.error("Could not load page visibility"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key, val) => {
    setVis((v) => ({ ...(v || {}), [key]: val }));
  };

  const save = async () => {
    if (!vis) return;
    setBusy(true);
    try {
      const payload = {};
      PAGE_TOGGLES.forEach(({ key }) => { if (key in vis) payload[key] = !!vis[key]; });
      const { data } = await adminApi.put("/admin/page-visibility", payload);
      setVis(data.visibility);
      // Bust the cached public config so AppShell picks it up on next nav
      invalidateSiteConfig();
      clearPageVisibilityCache();
      toast.success("Page visibility saved — disabled pages will redirect to Home on next visitor load");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-amber-400/30 p-5 space-y-4 mt-4" data-testid="fe-page-visibility-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2"><Globe className="h-4 w-4"/>Page Visibility</h3>
          <p className="text-xs text-zinc-400 mt-1">
            Toggle individual front-end pages on/off. Disabled pages 404 → Home for visitors and disappear from the site nav. Useful for seasonal pages (Tourism in winter), staging a redesign, or compliance pauses.
          </p>
        </div>
        <Button
          data-testid="fe-page-visibility-save"
          onClick={save}
          disabled={busy || loading || !vis}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Save className="h-4 w-4 mr-1.5"/>Save Visibility</>}
        </Button>
      </div>

      {loading || !vis ? (
        <div className="py-6 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline"/></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="fe-page-visibility-grid">
          {PAGE_TOGGLES.map(({ key, label, hint, path }) => {
            const enabled = vis[key] !== false; // missing key = visible (fallback rule)
            return (
              <div
                key={key}
                className={`rounded-lg border p-3 flex items-start gap-3 transition-colors ${enabled ? "bg-zinc-950 border-zinc-800" : "bg-red-950/20 border-red-800/40"}`}
                data-testid={`fe-page-toggle-${key}`}
              >
                <Switch
                  id={`fe-page-vis-${key}`}
                  checked={enabled}
                  onCheckedChange={(v) => toggle(key, v)}
                  data-testid={`fe-page-toggle-${key}-switch`}
                />
                <div className="flex-1 min-w-0">
                  <label htmlFor={`fe-page-vis-${key}`} className="text-sm font-bold text-zinc-100 cursor-pointer block leading-tight">{label}</label>
                  <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{hint}</div>
                  <a href={path} target="_blank" rel="noreferrer" className="text-[10px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-0.5 mt-1" data-testid={`fe-page-link-${key}`}><ExternalLink className="h-2.5 w-2.5"/>{path}</a>
                </div>
                <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${enabled ? "bg-emerald-900/40 text-emerald-300" : "bg-red-900/40 text-red-300"}`}>
                  {enabled ? "Visible" : "Hidden"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------
 * v2.7.65+E192 · PublicWebsiteLogoCard
 * ------------------------------------------------------------------
 * Website Management → Global tab card that lets an authorized admin
 * upload the PUBLIC marketing logo displayed on the customer-facing
 * navigation menu. Writes to `site_config.global.logo_url` — the same
 * field the marketing frontend already reads. Does NOT affect the
 * admin console sidebar, admin login page, or any other imagery.
 *
 *  • Max upload: 20 MB (server-enforced too)
 *  • Accepts: JPEG / PNG / GIF / WebP / SVG
 *  • Preview before save + Cancel option
 *  • Reset button reverts to the built-in default logo
 *  • Persists across refresh, sign-out, restart, and future sessions.
 *  • Emits `rtg:public-logo-updated` so preview panels can refresh. */
function PublicWebsiteLogoCard() {
  const [current, setCurrent] = useState({ url: "", updated_at: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/public-logo");
        if (!cancelled) setCurrent({ url: data?.url || "", updated_at: data?.updated_at || null });
      } catch {
        if (!cancelled) setCurrent({ url: "", updated_at: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  const onFile = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!CONSOLE_LOGO_ACCEPTED.includes(f.type)) {
      setErr("Please choose an image file (JPEG, PNG, GIF, WebP, or SVG).");
      e.target.value = "";
      return;
    }
    if (f.size > CONSOLE_LOGO_MAX_BYTES) {
      const mb = (f.size / (1024 * 1024)).toFixed(1);
      setErr(`File is too large (${mb} MB). Max is 20 MB.`);
      e.target.value = "";
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
    setSelected(f);
  };

  const cancel = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setSelected(null);
    setErr("");
    const el = document.querySelector('[data-testid="public-website-logo-file"]');
    if (el) el.value = "";
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", selected, selected.name);
      const { data } = await adminApi.post("/admin/public-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCurrent({ url: data?.url || "", updated_at: data?.updated_at || null });
      cancel();
      toast.success("Public website logo updated");
      try { window.dispatchEvent(new Event("rtg:public-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to save logo";
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!current.url) return;
    if (!window.confirm("Reset the PUBLIC website logo to the default Buller Taxi header image? Customers will see the change on their next page load.")) return;
    setClearing(true);
    setErr("");
    try {
      await adminApi.delete("/admin/public-logo");
      setCurrent({ url: "", updated_at: null });
      toast.success("Public website logo reset to default");
      try { window.dispatchEvent(new Event("rtg:public-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to reset";
      setErr(String(msg));
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card className="bg-zinc-950 border border-amber-500/40 p-4 space-y-3" data-testid="fe-public-website-logo">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Public Website Logo</div>
          <div className="text-[11px] text-zinc-400 max-w-lg">
            Customer-facing logo shown on the PUBLIC marketing site navigation menu
            (bullertaxi.com). Uploading here swaps the logo visitors see across the
            marketing pages. Does NOT change the admin console sidebar, login pages,
            or customer app icons. Max 20 MB — JPEG, PNG, GIF, WebP, or SVG.
          </div>
        </div>
        {current.updated_at && (
          <div className="text-[10px] text-zinc-500 whitespace-nowrap">
            Last saved: {new Date(current.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="public-website-logo-current-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Current Public Website Logo</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
            ) : current.url ? (
              <img
                src={current.url}
                alt="Current public website logo"
                className="max-h-28 max-w-full object-contain"
                data-testid="public-website-logo-current-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Using default Buller Taxi header image</span>
            )}
          </div>
          {current.url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              disabled={clearing}
              data-testid="public-website-logo-reset"
              className="text-red-300 hover:text-red-200 hover:bg-red-900/20 self-start text-[11px]"
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <RotateCcw className="h-3 w-3 mr-1"/>}
              Reset to default
            </Button>
          )}
        </div>

        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="public-website-logo-upload-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Upload Replacement</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {preview ? (
              <img
                src={preview}
                alt="New public website logo preview"
                className="max-h-28 max-w-full object-contain"
                data-testid="public-website-logo-preview-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Preview appears here after selection</span>
            )}
          </div>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            onChange={onFile}
            data-testid="public-website-logo-file"
            className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs file:text-zinc-100 file:bg-zinc-800 file:border-0 file:mr-3 file:rounded file:px-2 file:py-1"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={!selected || saving}
              data-testid="public-website-logo-save"
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Save className="h-3.5 w-3.5 mr-1"/>}
              Save Logo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancel}
              disabled={!selected || saving}
              data-testid="public-website-logo-cancel"
              className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
          </div>
          {err && (
            <div
              className="text-[11px] text-red-300 bg-red-950/30 border border-red-900/40 rounded px-2 py-1"
              data-testid="public-website-logo-error"
            >
              {err}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


/* ------------------------------------------------------------------
 * v2.7.65+E193 · LoginPageLogoCard
 * ------------------------------------------------------------------
 * Website Management → Global tab card that lets an authorized admin
 * upload the hero logo shown on the RIDER, DRIVER, DISPATCH/ADMIN, and
 * CUSTOMER sign-in pages. All four pages source their mark from the
 * same `global.taxi_commander_logo_url` site-config field, so ONE
 * upload here updates every login screen. Never affects the public
 * marketing logo (E192), admin console sidebar (E188), customer app
 * icons, or any other customer-facing branding. */
function LoginPageLogoCard() {
  const [current, setCurrent] = useState({ url: "", updated_at: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/login-logo");
        if (!cancelled) setCurrent({ url: data?.url || "", updated_at: data?.updated_at || null });
      } catch {
        if (!cancelled) setCurrent({ url: "", updated_at: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  const onFile = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!CONSOLE_LOGO_ACCEPTED.includes(f.type)) {
      setErr("Please choose an image file (JPEG, PNG, GIF, WebP, or SVG).");
      e.target.value = "";
      return;
    }
    if (f.size > CONSOLE_LOGO_MAX_BYTES) {
      const mb = (f.size / (1024 * 1024)).toFixed(1);
      setErr(`File is too large (${mb} MB). Max is 20 MB.`);
      e.target.value = "";
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
    setSelected(f);
  };

  const cancel = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setSelected(null);
    setErr("");
    const el = document.querySelector('[data-testid="login-page-logo-file"]');
    if (el) el.value = "";
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", selected, selected.name);
      const { data } = await adminApi.post("/admin/login-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCurrent({ url: data?.url || "", updated_at: data?.updated_at || null });
      cancel();
      toast.success("Login page logo updated");
      try { window.dispatchEvent(new Event("rtg:login-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to save logo";
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!current.url) return;
    if (!window.confirm("Reset the LOGIN-PAGE logo to the default Buller Taxi Commander image? Every sign-in page will change on the next load.")) return;
    setClearing(true);
    setErr("");
    try {
      await adminApi.delete("/admin/login-logo");
      setCurrent({ url: "", updated_at: null });
      toast.success("Login page logo reset to default");
      try { window.dispatchEvent(new Event("rtg:login-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to reset";
      setErr(String(msg));
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card className="bg-zinc-950 border border-amber-500/40 p-4 space-y-3" data-testid="fe-login-page-logo">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Login Page Logo</div>
          <div className="text-[11px] text-zinc-400 max-w-lg">
            Hero mark shown on the RIDER, DRIVER, DISPATCH/ADMIN, and CUSTOMER sign-in
            pages. One upload here updates every login screen at once (they all source
            the same field). Does NOT change the public marketing site logo, admin
            console sidebar, or any other customer-facing branding. Max 20 MB —
            JPEG, PNG, GIF, WebP, or SVG.
          </div>
        </div>
        {current.updated_at && (
          <div className="text-[10px] text-zinc-500 whitespace-nowrap">
            Last saved: {new Date(current.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="login-page-logo-current-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Current Login Page Logo</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
            ) : current.url ? (
              <img
                src={current.url}
                alt="Current login page logo"
                className="max-h-28 max-w-full object-contain"
                data-testid="login-page-logo-current-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Using default Buller Taxi Commander image</span>
            )}
          </div>
          {current.url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              disabled={clearing}
              data-testid="login-page-logo-reset"
              className="text-red-300 hover:text-red-200 hover:bg-red-900/20 self-start text-[11px]"
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <RotateCcw className="h-3 w-3 mr-1"/>}
              Reset to default
            </Button>
          )}
        </div>

        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="login-page-logo-upload-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Upload Replacement</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {preview ? (
              <img
                src={preview}
                alt="New login page logo preview"
                className="max-h-28 max-w-full object-contain"
                data-testid="login-page-logo-preview-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Preview appears here after selection</span>
            )}
          </div>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            onChange={onFile}
            data-testid="login-page-logo-file"
            className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs file:text-zinc-100 file:bg-zinc-800 file:border-0 file:mr-3 file:rounded file:px-2 file:py-1"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={!selected || saving}
              data-testid="login-page-logo-save"
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Save className="h-3.5 w-3.5 mr-1"/>}
              Save Logo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancel}
              disabled={!selected || saving}
              data-testid="login-page-logo-cancel"
              className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
          </div>
          {err && (
            <div
              className="text-[11px] text-red-300 bg-red-950/30 border border-red-900/40 rounded px-2 py-1"
              data-testid="login-page-logo-error"
            >
              {err}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


/* ------------------------------------------------------------------
 * v2.7.65+E188 · AdminConsoleLogoCard
 * ------------------------------------------------------------------
 * Website Management → Global tab card that lets an authorized admin
 * upload a logo displayed above the LEFT-SIDE admin sidebar. Applies
 * ONLY to the internal admin console — the public marketing logo,
 * customer booking pages, login pages, and customer-facing branding
 * are untouched.
 *
 *  • Max upload: 20 MB (server-enforced too)
 *  • Accepts: JPEG / PNG / GIF / WebP / SVG
 *  • Preview before save + Cancel option
 *  • Persists via POST /api/admin/console-logo, survives refresh,
 *    logout, restart, and future sessions.
 *  • Emits `rtg:admin-console-logo-updated` so open admin tabs
 *    refresh the sidebar image without needing a page reload. */
const CONSOLE_LOGO_MAX_BYTES = 20 * 1024 * 1024;
const CONSOLE_LOGO_ACCEPTED = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml",
];

function AdminConsoleLogoCard() {
  const [current, setCurrent] = useState({ url: "", updated_at: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // File
  const [preview, setPreview] = useState("");        // object URL for the pending upload
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/console-logo");
        if (!cancelled) setCurrent({ url: data?.url || "", updated_at: data?.updated_at || null });
      } catch {
        if (!cancelled) setCurrent({ url: "", updated_at: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Clean up preview object URLs when selection changes / unmounts
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  const onFile = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!CONSOLE_LOGO_ACCEPTED.includes(f.type)) {
      setErr("Please choose an image file (JPEG, PNG, GIF, WebP, or SVG).");
      e.target.value = "";
      return;
    }
    if (f.size > CONSOLE_LOGO_MAX_BYTES) {
      const mb = (f.size / (1024 * 1024)).toFixed(1);
      setErr(`File is too large (${mb} MB). Max is 20 MB.`);
      e.target.value = "";
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
    setSelected(f);
  };

  const cancel = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setSelected(null);
    setErr("");
    const el = document.querySelector('[data-testid="admin-console-logo-file"]');
    if (el) el.value = "";
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", selected, selected.name);
      const { data } = await adminApi.post("/admin/console-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = { url: data?.url || "", updated_at: data?.updated_at || null };
      setCurrent(updated);
      cancel();
      toast.success("Admin Console Logo updated");
      try { window.dispatchEvent(new Event("rtg:admin-console-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to save logo";
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!current.url) return;
    if (!window.confirm("Reset the Admin Console Logo back to the default Buller Taxi Commander image?")) return;
    setClearing(true);
    setErr("");
    try {
      await adminApi.delete("/admin/console-logo");
      setCurrent({ url: "", updated_at: null });
      toast.success("Admin Console Logo reset to default");
      try { window.dispatchEvent(new Event("rtg:admin-console-logo-updated")); } catch { /* ignore */ }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to reset";
      setErr(String(msg));
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-3" data-testid="fe-admin-console-logo">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Admin Console Logo</div>
          <div className="text-[11px] text-zinc-500 max-w-lg">
            Internal branding shown ABOVE the left-side admin sidebar throughout the admin console.
            Does NOT change the public Buller Taxi website logo, customer booking pages, login pages,
            or any customer-facing branding. Max 20 MB — JPEG, PNG, GIF, WebP, or SVG.
          </div>
        </div>
        {current.updated_at && (
          <div className="text-[10px] text-zinc-500 whitespace-nowrap">
            Last saved: {new Date(current.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Currently saved logo */}
        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="admin-console-logo-current-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Current Admin Console Logo</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
            ) : current.url ? (
              <img
                src={current.url}
                alt="Current admin console logo"
                className="max-h-28 max-w-full object-contain"
                data-testid="admin-console-logo-current-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Using default Buller Taxi Commander image</span>
            )}
          </div>
          {current.url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              disabled={clearing}
              data-testid="admin-console-logo-reset"
              className="text-red-300 hover:text-red-200 hover:bg-red-900/20 self-start text-[11px]"
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <RotateCcw className="h-3 w-3 mr-1"/>}
              Reset to default
            </Button>
          )}
        </div>

        {/* Upload + preview */}
        <div className="border border-zinc-800 rounded bg-zinc-900/60 p-3 flex flex-col gap-2" data-testid="admin-console-logo-upload-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Upload Replacement</div>
          <div className="flex items-center justify-center bg-zinc-950/60 rounded border border-zinc-800 h-32">
            {preview ? (
              <img
                src={preview}
                alt="New admin console logo preview"
                className="max-h-28 max-w-full object-contain"
                data-testid="admin-console-logo-preview-img"
              />
            ) : (
              <span className="text-[11px] text-zinc-500 italic">Preview appears here after selection</span>
            )}
          </div>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            onChange={onFile}
            data-testid="admin-console-logo-file"
            className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs file:text-zinc-100 file:bg-zinc-800 file:border-0 file:mr-3 file:rounded file:px-2 file:py-1"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={!selected || saving}
              data-testid="admin-console-logo-save"
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Save className="h-3.5 w-3.5 mr-1"/>}
              Save Logo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancel}
              disabled={!selected || saving}
              data-testid="admin-console-logo-cancel"
              className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
          </div>
          {err && (
            <div
              className="text-[11px] text-red-300 bg-red-950/30 border border-red-900/40 rounded px-2 py-1"
              data-testid="admin-console-logo-error"
            >
              {err}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
